/**
 * Port of KinetiQE/src/lib/report/plantarModel.ts — change both together.
 *
 * The plantar pages of the report — the static test (standing) and the
 * walking (dynamic) test — as rows of text, so the web report and the printed
 * one lay out the same table from the same scan document.
 *
 * The admin-panel report keeps a JavaScript copy
 * (Kinetiq-Dashboard/…/lib/report/plantarModel.js), held to this one by
 * pose/webReportParity.test.ts. Pure: no React, no DOM.
 */

/** Cavanagh & Rodgers arch index bands, as plantarMetrics.ts. */
export const ARCH_HIGH_MAX = 0.21;
export const ARCH_NORMAL_MAX = 0.26;
/** Normal foot progression (Fick) band, degrees of toe-out. */
export const FICK_NORMAL = [5, 18];

export function archTypeOf(index) {
  if (typeof index !== "number" || !Number.isFinite(index) || index <= 0) return null;
  if (index <= ARCH_HIGH_MAX) return "High arch";
  if (index < ARCH_NORMAL_MAX) return "Normal";
  return "Low / flat";
}

const num = (v, unit = "", d = 0) =>
  typeof v === "number" && Number.isFinite(v) ? `${v.toFixed(d)}${unit}` : "—";


/** Narrow / standard / wide, from foot width over length (adult feet run about 0.36–0.42). */
export function widthClassOf(widthMm, lengthMm) {
  if (typeof widthMm !== "number" || typeof lengthMm !== "number" || !(lengthMm > 0) || !(widthMm > 0)) return null;
  const r = widthMm / lengthMm;
  return r < 0.36 ? "narrow" : r > 0.42 ? "wide" : "standard";
}

/** Shoe size from foot length (Paris point): EU = 1.5 × (cm + 1.5); UK = EU − 33.5. A guide, not a fitting. */
export function shoeSizeOf(lengthMm) {
  if (typeof lengthMm !== "number" || !(lengthMm > 120)) return null;
  const eu = Math.round(1.5 * (lengthMm / 10 + 1.5) * 2) / 2;
  return `EU ${eu} / UK ${Math.round((eu - 33.5) * 2) / 2}`;
}

/** Footprint dimensions, when the scan measured them (older scans did not). */
/**
 * `contactWidth(foot)`: that foot's widths are of the sole in contact with the
 * podoscope glass, whose outer edge lights faintly — they read narrower than
 * the foot, so no width class (narrow / standard / wide) is drawn from them.
 */
function dimensionRows(L, R, { contactWidth = () => false } = {}) {
  if (!(L?.footLengthMm || R?.footLengthMm)) return [];
  const mm = v => num(v, " mm");
  // A width read from the sole in contact is not the foot's (under a blue
  // light it read 56 mm on a 239 mm foot): such a foot's widths are left out.
  const fromContact = f => !!(f && contactWidth(f));
  const contact = [L, R].some(f => f?.footWidthMm && fromContact(f));
  const width = f => {
    if (!f?.footWidthMm || fromContact(f)) return "—";
    const c = widthClassOf(f.footWidthMm, f.footLengthMm);
    return `${mm(f.footWidthMm)}${c ? ` (${c})` : ""}`;
  };
  const own = (f, v) => (fromContact(f) ? "—" : mm(v));
  return [
    { label: "Foot length", left: mm(L?.footLengthMm), right: mm(R?.footLengthMm), reference: "footprint, heel to toe" },
    { label: "Foot width", left: width(L), right: width(R), ...(contact ? { reference: "— where the foot's outline was not found" } : {}) },
    { label: "Ball width", left: own(L, L?.ballWidthMm), right: own(R, R?.ballWidthMm) },
    { label: "Heel width", left: own(L, L?.heelWidthMm), right: own(R, R?.heelWidthMm) },
    { label: "Shoe size", left: shoeSizeOf(L?.footLengthMm) ?? "—", right: shoeSizeOf(R?.footLengthMm) ?? "—", reference: "guide, from length" },
  ];
}

/** The camera foot-size table's caption: what each foot was measured from. */
export function cameraFootSizeCaption(fs) {
  const outlined = fs?.left?.measuredFrom === "outline" || fs?.right?.measuredFrom === "outline";
  return `Foot size — measured from the podoscope photograph (${outlined ? "the outline of each foot, found by AI segmentation" : "the soles in contact with the glass"})`;
}

/**
 * Foot size measured on the calibrated podoscope (scan.footSize), as its own
 * left / right table — for a scan the mat did not measure. Null when there is
 * no camera measurement, or the mat already gave the dimensions.
 * Kept in step with KinetiQE/src/lib/report/plantarModel.ts.
 */
export function cameraFootSizeRows(fs, a) {
  if (a?.feet?.left?.footLengthMm || a?.feet?.right?.footLengthMm) return null;
  const L = fs?.left ?? null, R = fs?.right ?? null;
  // A foot's widths are its own only from its AI outline; from the lit contact they read under it.
  const rows = dimensionRows(L, R, { contactWidth: f => f.measuredFrom !== "outline" });
  if (!rows.length) return null;
  return [...rows, { label: "Contact area", left: num(L?.areaCm2, " cm²", 1), right: num(R?.areaCm2, " cm²", 1) }];
}

/**
 * The static test, one row per parameter with a column per foot. Null when the
 * mat was not used. When the scan predates per-foot readings, the whole-mat
 * figures are shown once, under "Both feet", rather than printed as if they
 * were one foot's.
 */
export function staticPlantarRows(a) {
  if (!a) return null;
  const L = a.feet?.left ?? null, R = a.feet?.right ?? null;
  const share = v => num(v, " %");
  if (L || R) {
    const fore = f => (f && typeof f.forefootPct === "number" ? f.forefootPct : null);
    return {
      perFoot: true,
      rows: [
        { label: "Load share", left: share(a.bilateral?.left), right: share(a.bilateral?.right), reference: "50 ± 5 %" },
        { label: "Forefoot load", left: num(fore(L), " %"), right: num(fore(R), " %") },
        { label: "Hindfoot load", left: num(fore(L) === null ? null : 100 - fore(L), " %"), right: num(fore(R) === null ? null : 100 - fore(R), " %") },
        { label: "Foot area", left: num(L?.footAreaCm2, " cm²"), right: num(R?.footAreaCm2, " cm²") },
        ...dimensionRows(L, R),
        { label: "Mean pressure", left: num(L?.meanPressureKpa, " kPa"), right: num(R?.meanPressureKpa, " kPa") },
        { label: "Peak pressure", left: num(L?.peakPressureKpa, " kPa"), right: num(R?.peakPressureKpa, " kPa") },
        { label: "Arch index", left: num(L?.archIndex, "", 2), right: num(R?.archIndex, "", 2), reference: `${ARCH_HIGH_MAX}–${ARCH_NORMAL_MAX} normal` },
        { label: "Arch type", left: archTypeOf(L?.archIndex) ?? "—", right: archTypeOf(R?.archIndex) ?? "—" },
      ],
    };
  }
  const both = v => ({ left: v, right: v });
  return {
    perFoot: false,
    rows: [
      { label: "Load share", left: share(a.bilateral?.left), right: share(a.bilateral?.right), reference: "50 ± 5 %" },
      { label: "Foot area (both feet)", ...both(num(a.footAreaCm2, " cm²")) },
      { label: "Mean pressure (both feet)", ...both(num(a.meanPressureKpa, " kPa")) },
      { label: "Peak pressure (both feet)", ...both(num(a.peakPressureKpa, " kPa")) },
    ],
  };
}

export function walkTestOf(scan) {
  const w = scan?.walkTest;
  if (!w || typeof w.capturedAt !== "string") return null;
  const img = k => (typeof w.images?.[k] === "string" && w.images[k] ? w.images[k] : null);
  const left = w.left && w.left.steps ? w.left : null;
  const right = w.right && w.right.steps ? w.right : null;
  if (!left && !right && !w.inconclusive) return null;
  return {
    capturedAt: w.capturedAt,
    inconclusive: w.inconclusive ?? null,
    progressionSource: w.progressionSource ?? null,
    observations: Array.isArray(w.observations) ? w.observations.filter(o => typeof o === "string") : [],
    stepCount: Array.isArray(w.steps) ? w.steps.length : (left?.steps ?? 0) + (right?.steps ?? 0),
    left, right,
    images: {
      leftPhases: img("leftPhases"), rightPhases: img("rightPhases"),
      leftFootprint: img("leftFootprint"), rightFootprint: img("rightFootprint"), curves: img("curves"),
    },
  };
}

const ARCH_LABEL = { high: "High arch", normal: "Normal", low: "Low / flat" };

function fickText(f) {
  if (!f || typeof f.fickDeg !== "number") return "—";
  const cls = f.fickClass === "in-toeing" ? " (in-toeing)" : f.fickClass === "out-toeing" ? " (out-toeing)" : " (normal)";
  return `${f.fickDeg.toFixed(1)}°${cls}`;
}

/** The walking test, one row per parameter with a column per foot. */
export function walkRows(w) {
  const L = w.left, R = w.right;
  const both = pick => ({ left: L ? pick(L) : "—", right: R ? pick(R) : "—" });
  return [
    { label: "Steps captured", ...both(f => String(f.steps ?? 0)) },
    { label: "Stance time", ...both(f => num(f.stanceS, " s", 2)) },
    { label: "Forefoot / rearfoot (F/R) ratio", ...both(f => num(f.frRatio, "", 2)) },
    { label: "Forefoot load", ...both(f => num(f.forefootPct, " %")) },
    { label: "Foot progression angle (Fick)", ...both(fickText), reference: `${FICK_NORMAL[0]}–${FICK_NORMAL[1]}° toe-out` },
    {
      label: "Arch index",
      ...both(f => typeof f.archIndex === "number"
        ? `${f.archIndex.toFixed(2)} (${ARCH_LABEL[f.archType ?? ""] ?? archTypeOf(f.archIndex) ?? "—"})` : "—"),
      reference: `${ARCH_HIGH_MAX}–${ARCH_NORMAL_MAX} normal`,
    },
    { label: "Mean pressure", ...both(f => num(f.meanKpa, " kPa", 1)) },
    { label: "Peak pressure", ...both(f => num(f.peakKpa, " kPa")) },
    { label: "Heel strike (share of stance)", ...both(f => num(f.phases?.heelStrike, " %")) },
    { label: "Mid-stance", ...both(f => num(f.phases?.midStance, " %")) },
    { label: "Toe-off", ...both(f => num(f.phases?.toeOff, " %")) },
  ];
}
